// AppCopyField's twin: a read-only value with a Copy button, for invitation links and codes. After a copy the button
// turns into a green "Copied" for two seconds, a status so it is announced. The help says who the value is for and how
// long it works. Ids end in `Field`.
import { type ReactNode, useEffect, useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './button'
import { cn } from './cn'
import { Icon, type IconName } from './icons'

type CopyFieldProps = {
  label: ReactNode
  value: string
  help?: ReactNode
  icon?: IconName
  onCopy?: (value: string) => void
  /** Holds the copied look, for previews. */
  copied?: boolean
  className?: string
  testId?: string
}

const COPIED_MS = 2000

export function CopyField({ label, value, help, icon = 'link', onCopy, copied, className, testId }: CopyFieldProps) {
  const { t } = useTranslation()
  const labelId = useId()
  const [justCopied, setJustCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(timer.current), [])
  const done = copied ?? justCopied

  const copy = () => {
    // The page may not be allowed the clipboard (an http origin, a denied permission); the value stays selectable.
    navigator.clipboard?.writeText(value).catch(() => {})
    setJustCopied(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setJustCopied(false), COPIED_MS)
    onCopy?.(value)
  }

  return (
    <div data-testid={testId} className={cn('flex min-w-0 flex-col gap-1.5', className)}>
      <span id={labelId} className="px-1 font-bold text-[13px] text-fg-muted leading-[18px]">
        {label}
      </span>
      <fieldset
        aria-labelledby={labelId}
        className="m-0 flex h-(--size-field) min-w-0 items-center gap-2 border-0 rounded-(--radius-field) bg-surface ps-3.5 pe-1.5 text-fg shadow-[inset_0_0_0_1px_var(--line-strong)] contrast-more:shadow-[inset_0_0_0_1px_var(--edge-contrast)]"
      >
        <Icon name={icon} aria-hidden className="size-4 shrink-0 text-fg-muted" />
        <span className="min-w-0 flex-1 truncate font-semibold text-[15px] tabular-nums">{value}</span>
        {done ? (
          <span
            role="status"
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-done-soft px-3.5 font-bold text-[14px] text-on-done-soft"
          >
            <Icon name="check" aria-hidden className="size-4" />
            {t('core.copy.copied')}
          </span>
        ) : (
          <Button
            variant="tonal"
            size="sm"
            onClick={copy}
            aria-describedby={labelId}
            data-testid={testId ? `${testId}_copyButton` : undefined}
          >
            <Icon name="copy" aria-hidden className="size-4" />
            {t('core.copy.copy')}
          </Button>
        )}
      </fieldset>
      {help && <p className="px-1 font-semibold text-caption text-fg-muted">{help}</p>}
    </div>
  )
}
